import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Star, Plus } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { inr, COMBO } from "@/lib/content";

export const ComboTag = ({ big }) => (
  <Link to="/combo" data-testid="combo-tag" className={`mt-2 inline-flex w-fit items-center gap-1 rounded-full border border-dashed border-[#B85D43] bg-[#F4E4DE] font-semibold text-[#701A24] transition-colors hover:bg-[#EBD3CA] ${big ? "px-4 py-1.5 text-sm" : "px-2.5 py-0.5 text-[11px]"}`}>
    2 for {inr(COMBO.pair)}{big && <span className="font-normal text-[#57534E]"> · build your combo →</span>}
  </Link>
);

const LABEL_TONE = {
  Bestseller: "bg-[#701A24] text-white",
  New: "bg-[#1B4332] text-white",
  "Limited Stock": "bg-[#B85D43] text-white",
  "Festive Favourite": "bg-[#D4AF37] text-[#1C1917]",
};

export const Stars = ({ value, size = "h-3.5 w-3.5" }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= Math.round(value) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#D6CFC6]"}`} />
    ))}
  </span>
);

export default function ProductCard({ p }) {
  const { addToCart, toggleWish, isWished } = useShop();
  const [color, setColor] = useState(p.colors[0]?.name);
  const firstSize = p.stock.M > 0 ? "M" : p.sizes.find((s) => p.stock[s] > 0) || "M";
  const totalStock = Object.values(p.stock).reduce((a, b) => a + b, 0);

  return (
    <div data-testid={`product-card-${p.slug}`} className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#E7E2DB] bg-white transition-shadow duration-300 hover:shadow-[0_18px_40px_-18px_rgba(112,26,36,0.35)]">
      <Link to={`/products/${p.slug}`} className="relative block aspect-[3/4] overflow-hidden bg-[#F4EFEA]">
        <img src={p.images[0]} alt={p.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top transition-all duration-700 group-hover:scale-105 group-hover:opacity-0" />
        <img src={p.images[1] || p.images[0]} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
        {p.label && <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${LABEL_TONE[p.label]}`}>{p.label}</span>}
      </Link>
      <button data-testid={`wishlist-toggle-${p.slug}`} onClick={() => toggleWish(p)} aria-label="Add to wishlist" className="absolute right-3 top-3 rounded-full bg-white/85 p-2 shadow-sm backdrop-blur-md transition-transform hover:scale-110">
        <Heart className={`h-4 w-4 ${isWished(p.slug) ? "fill-[#B85D43] text-[#B85D43]" : "text-[#44403C]"}`} />
      </button>
      <div className="flex flex-1 flex-col p-3.5 sm:p-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#B85D43]">{p.category_label}</p>
        <Link to={`/products/${p.slug}`} className="mt-1 line-clamp-2 text-sm font-medium leading-snug text-[#1C1917] hover:text-[#701A24] sm:text-[15px]">{p.name}</Link>
        {p.review_count > 0 && (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-[#78716C]"><Stars value={p.rating} /> {p.rating} ({p.review_count})</div>
        )}
        <div className="mt-2 flex flex-wrap items-baseline gap-x-2">
          <span className="text-base font-semibold text-[#1C1917]">{inr(p.price)}</span>
          <span className="text-xs text-[#A8A29E] line-through">{inr(p.mrp)}</span>
          <span className="text-xs font-semibold text-[#1B4332]">{p.discount_pct}% off</span>
        </div>
        {p.price === COMBO.item && <ComboTag />}
        <div className="mt-2.5 flex items-center gap-1.5">
          {p.colors.map((c) => (
            <button key={c.name} data-testid={`swatch-${p.slug}-${c.name.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setColor(c.name)} title={c.name} aria-label={c.name} style={{ background: c.hex }} className={`h-4 w-4 rounded-full border border-black/10 ring-offset-2 transition ${color === c.name ? "ring-1 ring-[#701A24]" : ""}`} />
          ))}
          {totalStock <= 8 && <span className="ml-auto text-[11px] font-medium text-[#B85D43]">Only {totalStock} left</span>}
        </div>
        <button data-testid={`quick-add-to-cart-${p.slug}`} onClick={() => addToCart(p, firstSize, color)} className="mt-3.5 flex w-full items-center justify-center gap-1.5 rounded-full border border-[#701A24] py-2.5 text-xs font-semibold text-[#701A24] transition-colors hover:bg-[#701A24] hover:text-white">
          <Plus className="h-3.5 w-3.5" /> Quick Add · {firstSize}
        </button>
      </div>
    </div>
  );
}
